import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Booking, getBooking } from '../../lib/bookings';
import BookingCustomerInfo from './BookingCustomerInfo';
import BookingServiceList from './BookingServiceList';
import BookingResourceList from './BookingResourceList';
import BookingPaymentSummary from './BookingPaymentSummary';
import BookingMaterialsTab from './BookingMaterialsTab';
import BookingFlatRatesTab from './BookingFlatRatesTab';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { getBookingCommissions } from '../../lib/bookingCommissions';

import BookingProfitLoss from './BookingProfitLoss';
import BookingCommissionSection from './BookingCommissionSection';
import { format } from 'date-fns';
import { updateBooking } from '../../lib/bookings';
import ApplyDiscountModal from '../ApplyDiscountModal';
import RecordPaymentModal from '../RecordPaymentModal';
import PaymentDetailsModal from '../PaymentDetailsModal';
import { Payment } from '../../lib/payments';
import { Clock, ExternalLink, Copy, CheckCircle, Star, ChevronDown, ChevronRight } from 'lucide-react';
import { generateFeedbackForBooking, generateFeedbackURL, getBookingFeedback, isFeedbackEnabled, type BookingFeedback } from '../../lib/feedback';
import { getAllFeedbackFormFields, type FeedbackFormField } from '../../lib/feedbackForm';

interface DynamicFeedbackResponse {
  [fieldKey: string]: string | number;
}

interface ExtendedBookingFeedback extends BookingFeedback {
  form_responses?: DynamicFeedbackResponse;
}

interface BookingContentProps {
  activeTab: string;
  booking: Booking;
  isSelectMaterialModalOpen: boolean;
  setIsSelectMaterialModalOpen: (isOpen: boolean) => void;
  onEditCustomer: () => void;
  onEditVehicle: (vehicleId: string) => void;
  onAddService: (vehicleId: string) => void;
  onRemoveResource: (resourceId: string) => Promise<void>;
  onAddResource: () => void;
  onApplyDiscount: () => void;
  onDeleteDiscount: (discountId: string) => Promise<void>;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  onAddressUpdate?: (address: string) => Promise<void>;
}

export default function BookingContent({
  activeTab,
  booking,
  isSelectMaterialModalOpen,
  setIsSelectMaterialModalOpen,
  onEditCustomer,
  onEditVehicle,
  onAddService,
  onRemoveResource,
  onAddResource,
  onApplyDiscount,
  onDeleteDiscount,
  onUpdate,
  onError,
  onAddressUpdate
}: BookingContentProps) {
  const { canAccess } = usePermissionCheck();
  const [isApplyDiscountModalOpen, setIsApplyDiscountModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [isPaymentDetailsModalOpen, setIsPaymentDetailsModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [timeFormData, setTimeFormData] = useState({
    drop_off_date: '',
    drop_off_time: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: ''
  });
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isEditingCustomerNotes, setIsEditingCustomerNotes] = useState(false);
  const [customerNotes, setCustomerNotes] = useState(booking.customer_notes || '');
  const [feedbackLink, setFeedbackLink] = useState<string | null>(null);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [feedbackCopied, setFeedbackCopied] = useState(false);
  const [bookingFeedback, setBookingFeedback] = useState<BookingFeedback | null>(null);
  const [showFeedbackLink, setShowFeedbackLink] = useState(false);
  const [showIndividualRatings, setShowIndividualRatings] = useState(false);
  const [isFeedbackEnabledForBusiness, setIsFeedbackEnabledForBusiness] = useState(true);
  const [feedbackFormFields, setFeedbackFormFields] = useState<FeedbackFormField[]>([]);
  const [allPaymentDetails, setAllPaymentDetails] = useState<any[]>([]);

  const dropOffDateErrorId = 'drop-off-date-error';
  const dropOffTimeErrorId = 'drop-off-time-error';
  const startDateErrorId = 'start-date-error';
  const startTimeErrorId = 'start-time-error';
  const endDateErrorId = 'end-date-error';
  const endTimeErrorId = 'end-time-error';

  // Initialize form data when booking changes
  React.useEffect(() => {
    if (booking) {
      setTimeFormData({
        drop_off_date: booking.drop_off_date || '',
        drop_off_time: booking.drop_off_time || '',
        start_date: booking.start_date || '',
        start_time: booking.start_time || '',
        end_date: booking.end_date || '',
        end_time: booking.end_time || ''
      });
      setCustomerNotes(booking.customer_notes || '');
    }
  }, [booking]);

  // Check if feedback is enabled for this business
  React.useEffect(() => {
    const checkFeedbackEnabled = async () => {
      if (booking.business_id) {
        try {
          const enabled = await isFeedbackEnabled(booking.business_id);
          setIsFeedbackEnabledForBusiness(enabled);
        } catch (error) {
          console.error('Error checking feedback enabled status:', error);
          setIsFeedbackEnabledForBusiness(false);
        }
      }
    };
    
    checkFeedbackEnabled();
  }, [booking.business_id]);

  // Generate feedback link for completed bookings
  React.useEffect(() => {
    const generateFeedbackLink = async () => {
      if (booking.status === 'completed' && isFeedbackEnabledForBusiness) {
        if (booking.feedback_token) {
          // If we already have a token, generate the URL
          const url = generateFeedbackURL(booking.feedback_token);
          setFeedbackLink(url);
        } else {
          // If completed but no token, try to generate one
          try {
            setIsGeneratingFeedback(true);
            const token = await generateFeedbackForBooking(booking.id);
            if (token) {
              const url = generateFeedbackURL(token);
              setFeedbackLink(url);
              // Try to refresh booking, but don't fail if it doesn't work
              try {
                await onUpdate();
              } catch (updateError) {
                console.warn('Could not refresh booking after feedback generation:', updateError);
                // Still show the link even if booking refresh fails
              }
            } else {
              console.error('Failed to generate feedback token');
            }
          } catch (error) {
            console.error('Error generating feedback link:', error);
          } finally {
            setIsGeneratingFeedback(false);
          }
        }
      } else {
        setFeedbackLink(null);
      }
    };

    generateFeedbackLink();
  }, [booking.status, booking.feedback_token, booking.id, isFeedbackEnabledForBusiness, onUpdate]);

  // Load feedback results for completed bookings
  React.useEffect(() => {
    const loadBookingFeedback = async () => {
      if (booking.status === 'completed') {
        try {
          const feedback = await getBookingFeedback(booking.id);
          setBookingFeedback(feedback);
        } catch (error) {
          console.error('Error loading booking feedback:', error);
        }
      } else {
        setBookingFeedback(null);
      }
    };

    loadBookingFeedback();
  }, [booking.status, booking.id]);

  // Load feedback form fields for this business
  React.useEffect(() => {
    const loadFeedbackFormFields = async () => {
      if (booking.business_id) {
        try {
          const fields = await getAllFeedbackFormFields(booking.business_id);
          setFeedbackFormFields(fields);
        } catch (error) {
          console.error('Error loading feedback form fields:', error);
          setFeedbackFormFields([]);
        }
      }
    };

    loadFeedbackFormFields();
  }, [booking.business_id]);

  const handleCopyFeedbackLink = async () => {
    if (feedbackLink) {
      try {
        await navigator.clipboard.writeText(feedbackLink);
        setFeedbackCopied(true);
        setTimeout(() => setFeedbackCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy feedback link:', error);
        onError('Failed to copy feedback link');
      }
    }
  };

  const handleGenerateFeedbackLink = async () => {
    try {
      setIsGeneratingFeedback(true);
      const token = await generateFeedbackForBooking(booking.id);
      if (token) {
        const url = generateFeedbackURL(token);
        setFeedbackLink(url);
        // Try to refresh booking, but don't fail if it doesn't work
        try {
          await onUpdate();
        } catch (updateError) {
          console.warn('Could not refresh booking after feedback generation:', updateError);
          // Still show the link even if booking refresh fails
        }
      } else {
        onError('Failed to generate feedback link');
      }
    } catch (error) {
      console.error('Error generating feedback link:', error);
      onError('Failed to generate feedback link');
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  // Helper component to render star ratings
  const StarRating = ({ rating, size = 'small' }: { rating: number; size?: 'small' | 'large' }) => {
    const starSize = size === 'large' ? 'h-5 w-5' : 'h-4 w-4';
    
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleViewInvoice = async () => {
    try {
      setIsLoadingInvoice(true);
      
      // Always fetch fresh booking data to ensure complete service details
      const freshBooking = await getBooking(booking.id);
      if (!freshBooking) {
        throw new Error('Could not load booking details');
      }

      // Create all payment details objects for the modal
      const allPayments: any[] = [];

      // Add existing payments with fresh booking data
      const existingPayments = (booking.payments || []).map(payment => {
        return {
          id: payment.id,
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: payment.amount,
          status: payment.status as 'pending' | 'completed' | 'voided',
          created_at: payment.created_at,
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
      });

      allPayments.push(...existingPayments);

      // If no payments exist, create a pending invoice
      if (!booking.payments || booking.payments.length === 0) {
        const pendingInvoice = {
          id: 'pending',
          business_id: freshBooking.business_id,
          booking_id: freshBooking.id,
          amount: freshBooking.total_amount || 0,
          status: 'pending' as const,
          created_at: new Date().toISOString(),
          notes: undefined,
          booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
          booking_title: freshBooking.title,
          booking_subtotal: freshBooking.subtotal,
          customer_first_name: freshBooking.customer?.first_name || '',
          customer_last_name: freshBooking.customer?.last_name || '',
          customer_email: freshBooking.customer?.email || '',
          booking_services: freshBooking.services?.map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            tax_amount: s.tax_amount,
            total_amount: s.total_amount,
            duration_minutes: s.duration_minutes,
            vehicle_make: s.vehicle_make || '',
            vehicle_model: s.vehicle_model || ''
          })) || [],
          booking_discounts: freshBooking.discounts?.map(d => ({
            id: d.id,
            title: d.title,
            type: d.type as 'percentage' | 'fixed',
            amount: d.amount
          })) || []
        };
        allPayments.push(pendingInvoice);
      } else {
        // Check if there's a remaining balance for partial payments
        const totalPaid = (booking.payments || [])
          .filter(p => p.status === 'completed')
          .reduce((sum, p) => sum + p.amount, 0);
        
        const remainingBalance = (freshBooking.total_amount || 0) - totalPaid;
        
        if (remainingBalance > 0) {
          // Create an unpaid invoice for the remaining balance
          const remainingInvoice = {
            id: 'pending-balance',
            business_id: freshBooking.business_id,
            booking_id: freshBooking.id,
            amount: remainingBalance,
            status: 'pending' as const,
            created_at: new Date().toISOString(),
            notes: undefined,
            booking_number: typeof freshBooking.booking_number === 'number' ? freshBooking.booking_number : parseInt(String(freshBooking.booking_number)) || 0,
            booking_title: freshBooking.title,
            booking_subtotal: freshBooking.subtotal,
            customer_first_name: freshBooking.customer?.first_name || '',
            customer_last_name: freshBooking.customer?.last_name || '',
            customer_email: freshBooking.customer?.email || '',
            booking_services: freshBooking.services?.map(s => ({
              id: s.id,
              name: s.name,
              description: s.description,
              price: s.price,
              tax_amount: s.tax_amount,
              total_amount: s.total_amount,
              duration_minutes: s.duration_minutes,
              vehicle_make: s.vehicle_make || '',
              vehicle_model: s.vehicle_model || ''
            })) || [],
            booking_discounts: freshBooking.discounts?.map(d => ({
              id: d.id,
              title: d.title,
              type: d.type as 'percentage' | 'fixed',
              amount: d.amount
            })) || []
          };
          allPayments.push(remainingInvoice);
        }
      }
      
      // Set selectedPayment to trigger PaymentDetailsModal to open
      setSelectedPayment(allPayments[0] || null);
      setIsPaymentDetailsModalOpen(true);

      // Store allPayments in a state for the modal
      setAllPaymentDetails(allPayments);
    } catch (err) {
      console.error('Error loading booking for invoice:', err);
      onError('Failed to load invoice details');
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const handleSaveTime = async () => {
    try {
      await updateBooking(booking.id, timeFormData);
      setIsEditingTime(false);
      await onUpdate();
    } catch (err) {
      console.error('Error updating times:', err);
      onError(err instanceof Error ? err.message : 'Failed to update times');
    }
  };

  const handleSaveCustomerNotes = async () => {
    try {
      await updateBooking(booking.id, { customer_notes: customerNotes });
      setIsEditingCustomerNotes(false);
      await onUpdate();
    } catch (err) {
      console.error('Error updating customer notes:', err);
      onError(err instanceof Error ? err.message : 'Failed to update customer notes');
    }
  };

  const handleColorChange = async (color: string) => {
    try {
      await updateBooking(booking.id, { color });
      await onUpdate();
    } catch (err) {
      console.error('Error changing color:', err);
      onError(err instanceof Error ? err.message : 'Failed to change color');
    }
  };

  if (activeTab === 'overview') {
    return (
      <div className="space-y-4">
        {/* Customer Feedback Section - Only show for completed bookings */}
        {booking.status === 'completed' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-1.5 border-b border-gray-200 bg-green-50">
              <h3 className="text-xs font-medium text-green-900 flex items-center">
                <CheckCircle className="h-3 w-3 mr-1.5" />
                Customer Feedback
              </h3>
            </div>
            <div className="p-3">
              {!isFeedbackEnabledForBusiness ? (
                // Show disabled message when feedback is turned off
                <div className="text-center py-4">
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md border">
                    Customer Feedback is disabled
                  </div>
                </div>
              ) : bookingFeedback ? (
                // Show feedback results when available and enabled
                <div className="space-y-3">
                  <div className="text-xs text-gray-600">
                    Submitted {format(new Date(bookingFeedback.submitted_at), 'MMM d, yyyy')}
                  </div>

                  {/* Dynamic Overall Rating Calculation */}
                  {(() => {
                    // Get responses from form_responses or fallback to legacy fields
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const ratingFields = feedbackFormFields.filter(field => field.field_type === 'rating');
                    
                    let totalRating = 0;
                    let ratingCount = 0;
                    
                    // Calculate overall rating from dynamic responses
                    if (Object.keys(responses).length > 0) {
                      ratingFields.forEach(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'number' && value > 0) {
                          totalRating += value;
                          ratingCount++;
                        }
                      });
                    } else {
                      // Fallback to legacy fields for backward compatibility
                      if (bookingFeedback.overall_quality_rating) {
                        totalRating += bookingFeedback.overall_quality_rating;
                        ratingCount++;
                      }
                      if (bookingFeedback.overall_experience_rating) {
                        totalRating += bookingFeedback.overall_experience_rating;
                        ratingCount++;
                      }
                    }
                    
                    const overallRating = ratingCount > 0 ? totalRating / ratingCount : 0;

                    return (
                      /* Overall Rating - Compact Single Line with Dropdown */
                      <div className="bg-gray-50 rounded-md p-2">
                        <button
                          onClick={() => setShowIndividualRatings(!showIndividualRatings)}
                          className="flex items-center justify-between w-full text-left hover:bg-gray-100 rounded transition-colors"
                        >
                          <div className="text-xs font-medium text-gray-700">Overall Rating</div>
                          <div className="flex items-center space-x-2">
                            <StarRating rating={Math.round(overallRating)} size="small" />
                            <span className="text-sm font-bold text-gray-900">
                              {overallRating.toFixed(1)}
                            </span>
                            {showIndividualRatings ? (
                              <ChevronDown className="h-3 w-3 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-gray-600" />
                            )}
                          </div>
                        </button>
                        
                        {/* Individual Ratings - Dynamic Dropdown Content */}
                        {showIndividualRatings && (
                          <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                            {/* Show dynamic rating fields */}
                            {Object.keys(responses).length > 0 ? (
                              ratingFields.map(field => {
                                const value = responses[field.field_key];
                                if (typeof value === 'number' && value > 0) {
                                  return (
                                    <div key={field.id} className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                      <span className="text-gray-700">{field.field_label}</span>
                                      <div className="flex items-center space-x-1">
                                        <StarRating rating={value} size="small" />
                                        <span className="text-gray-600 w-6">{value}/5</span>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })
                            ) : (
                              // Fallback to legacy display for old feedback
                              <>
                                {bookingFeedback.overall_quality_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Quality</span>
                                    <div className="flex items-center space-x-1">
                                      <StarRating rating={bookingFeedback.overall_quality_rating} size="small" />
                                      <span className="text-gray-600 w-6">{bookingFeedback.overall_quality_rating}/5</span>
                                    </div>
                                  </div>
                                )}
                                {bookingFeedback.overall_experience_rating && (
                                  <div className="flex items-center justify-between py-1.5 px-2 bg-white rounded text-xs">
                                    <span className="text-gray-700">Experience</span>
                                    <div className="flex items-center space-x-1">
                                      <StarRating rating={bookingFeedback.overall_experience_rating} size="small" />
                                      <span className="text-gray-600 w-6">{bookingFeedback.overall_experience_rating}/5</span>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Dynamic Text Responses */}
                  {(() => {
                    const responses = (bookingFeedback as ExtendedBookingFeedback).form_responses || {};
                    const textFields = feedbackFormFields.filter(field => field.field_type === 'text');
                    
                    // Show dynamic text responses
                    if (Object.keys(responses).length > 0) {
                      return textFields.map(field => {
                        const value = responses[field.field_key];
                        if (typeof value === 'string' && value.trim()) {
                          return (
                            <div key={field.id} className="space-y-1">
                              <div className="text-xs font-medium text-gray-600">{field.field_label}:</div>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                                "{value}"
                              </div>
                            </div>
                          );
                        }
                        return null;
                      });
                    } else {
                      // Fallback to legacy improvement suggestions
                      if (bookingFeedback.improvement_suggestions) {
                        return (
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-gray-600">Suggestions:</div>
                            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded italic">
                              "{bookingFeedback.improvement_suggestions}"
                            </div>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}

                  {/* Feedback Link Section - Collapsible */}
                  {feedbackLink && (
                    <div className="border-t border-gray-200 pt-2">
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <span>Share link</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Show no feedback message and link generation when enabled but no feedback yet
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">No feedback yet.</p>
                  
                  {/* Show feedback link for sharing - Collapsible */}
                  {feedbackLink && (
                    <div>
                      <button
                        onClick={() => setShowFeedbackLink(!showFeedbackLink)}
                        className="flex items-center justify-between w-full text-left py-1 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors border-t border-gray-200"
                      >
                        <span>Share link to collect feedback</span>
                        {showFeedbackLink ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      
                      {showFeedbackLink && (
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center space-x-1 p-2 bg-gray-50 rounded border text-xs">
                            <input
                              type="text"
                              value={feedbackLink}
                              readOnly
                              className="flex-1 bg-transparent border-none focus:outline-none text-gray-700"
                            />
                            <button
                              onClick={handleCopyFeedbackLink}
                              className="flex items-center px-2 py-1 font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              {feedbackCopied ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                            <a
                              href={feedbackLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-2 py-1 font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Preview
                            </a>
                          </div>
                          <p className="text-xs text-gray-500">
                            💡 Use <code className="bg-gray-200 px-1 rounded">[[feedback_link]]</code> in SMS automations
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generate link button if no link exists and feedback is enabled */}
                  {!feedbackLink && (
                    <button
                      onClick={handleGenerateFeedbackLink}
                      disabled={isGeneratingFeedback}
                      className="w-full px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGeneratingFeedback ? 'Generating...' : 'Generate Feedback Link'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Date and Time Information */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-900">Schedule</h3>
              {canAccess.editBookingDetails && (
                <button
                  onClick={() => setIsEditingTime(!isEditingTime)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {isEditingTime ? 'Cancel' : 'Edit'}
                </button>
              )}
            </div>
          </div>
          <div className="p-2">
            {isEditingTime && canAccess.editBookingDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Date</label>
                    <input
                      type="date"
                      value={timeFormData.drop_off_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Drop-off Time</label>
                    <input
                      type="time"
                      value={timeFormData.drop_off_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, drop_off_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={timeFormData.start_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={timeFormData.start_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={timeFormData.end_date}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Time</label>
                    <input
                      type="time"
                      value={timeFormData.end_time}
                      onChange={(e) => setTimeFormData(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full h-8 px-2 text-xs border border-gray-300 rounded-md focus:ring-gray-900 focus:border-gray-900"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveTime}
                    className="w-full px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <div className="text-xs text-gray-500">Drop-off</div>
                  <div className="mt-0.5 text-sm text-gray-900 leading-snug">
                    {booking.drop_off_date && booking.drop_off_time ? (
                      <>
                        {format(new Date(`${booking.drop_off_date}T${booking.drop_off_time}`), 'MMM d, yyyy')}
                        <span className="text-gray-500 ml-1">
                          {format(new Date(`2000-01-01T${booking.drop_off_time}`), 'h:mm a')}
                        </span>
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Start</div>
                  <div className="mt-0.5 text-sm text-gray-900 leading-snug">
                    {booking.start_date && booking.start_time ? (
                      <>
                        {format(new Date(`${booking.start_date}T${booking.start_time}`), 'MMM d, yyyy')}
                        <span className="text-gray-500 ml-1">
                          {format(new Date(`2000-01-01T${booking.start_time}`), 'h:mm a')}
                        </span>
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">End</div>
                  <div className="mt-0.5 text-sm text-gray-900 leading-snug">
                    {booking.end_date && booking.end_time ? (
                      <>
                        {format(new Date(`${booking.end_date}T${booking.end_time}`), 'MMM d, yyyy')}
                        <span className="text-gray-500 ml-1">
                          {format(new Date(`2000-01-01T${booking.end_time}`), 'h:mm a')}
                        </span>
                      </>
                    ) : (
                      'Not specified'
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Add duration summary */}
            {!isEditingTime && booking.services && booking.services.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">Total Duration</div>
                <div className="mt-0.5 text-sm text-gray-900 leading-snug flex items-center">
                  <Clock className="h-4 w-4 mr-1.5 text-gray-400" />
                  {(() => {
                    const totalMinutes = booking.services.reduce((total, service) => {
                      let minutes = service.duration_minutes || 0;
                      if (service.addons) {
                        minutes += service.addons.reduce((addonTotal, addon) => 
                          addonTotal + (addon.duration_minutes || 0), 0);
                      }
                      return total + minutes;
                    }, 0);
                    
                    return totalMinutes >= 1440 
                      ? `${Math.round(totalMinutes / 1440)} days`
                      : `${Math.round(totalMinutes / 60)} hours`;
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <BookingCustomerInfo
          customer={{
            id: booking.customer?.id || '',
            first_name: booking.customer?.first_name || '',
            last_name: booking.customer?.last_name || '',
            email: booking.customer?.email || null,
            phone: booking.customer?.phone || '',
            business_id: booking.business_id,
            type: 'person',
            address: null,
            source: null,
            tags: [],
            last_interaction: null,
            lifetime_value: 0,
            next_service_date: null,
            referral_source: null,
            preferences: {},
            status: 'active',
            amount_spent: 0,
            credit_balance: 0,
            notes: null,
            tax_exempt: false,
            workflow_exempt: false,
            carfax_exempt: false,
            receive_reminders: true,
            receive_offers: true,
            created_at: booking.created_at,
            updated_at: booking.updated_at,
            vehicles: booking.customer?.vehicles?.map(v => ({
              ...v,
              mileage: null,
              notes: null,
              last_service: null
            }))
          }}
          onEdit={canAccess.editBookingDetails ? onEditCustomer : undefined}
          bookingAddress={booking.booking_address || ''}
          onEditBookingAddress={canAccess.editBookingDetails ? onAddressUpdate : undefined}
          mobile_booking={booking.mobile_booking || false}
          canEdit={canAccess.editBookingDetails}
        />

        {/* Customer Notes Section */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900">Customer Notes</h3>
            {canAccess.editBookingDetails && (
              <button 
                onClick={() => setIsEditingCustomerNotes(!isEditingCustomerNotes)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {isEditingCustomerNotes ? 'Cancel' : 'Edit'}
              </button>
            )}
          </div>
          <div className="p-4">
            {isEditingCustomerNotes ? (
              <div className="space-y-3">
                <textarea
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="Enter any notes about the customer or booking"
                  className="w-full h-32 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveCustomerNotes}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
                  >
                    Save Notes
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                {booking.customer_notes ? booking.customer_notes : 'No customer notes'}
              </div>
            )}
          </div>
        </div>

        <BookingServiceList
          services={booking.services || []}
          onAddService={canAccess.editBookingDetails ? onAddService : undefined}
          onEditVehicle={canAccess.editBookingDetails ? onEditVehicle : undefined}
          onUpdate={onUpdate}
          onError={onError}
          customer={booking.customer}
          showSalesInfo={canAccess.bookingSalesInfo}
          canEdit={canAccess.editBookingDetails}
        />

        <BookingResourceList
          resources={booking.resources || []}
          onRemoveResource={canAccess.editBookingDetails ? onRemoveResource : undefined}
          onAddResource={canAccess.editBookingDetails ? onAddResource : undefined}
        />

        {/* Commission Section - Only shows if there are commission-based resources */}
        <BookingCommissionSection
          booking={booking}
          onUpdate={onUpdate}
          onError={onError}
        />

        {canAccess.bookingSalesInfo && (
          <BookingPaymentSummary
            subtotal={booking.subtotal || 0}
            discounts={booking.discounts}
            taxAmount={booking.tax_amount || 0}
            totalAmount={booking.total_amount || 0}
            payments={booking.payments}
            onApplyDiscount={onApplyDiscount}
            onDeleteDiscount={onDeleteDiscount}
            onRecordPayment={() => setIsRecordPaymentModalOpen(true)}
            onViewInvoice={handleViewInvoice}
          />
        )}

        <ApplyDiscountModal
          isOpen={isApplyDiscountModalOpen}
          onClose={() => setIsApplyDiscountModalOpen(false)}
          bookingId={booking.id}
          bookingTotal={booking.total_amount}
          onApply={onUpdate}
        />

        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => setIsRecordPaymentModalOpen(false)}
          onSuccess={onUpdate}
          bookingId={booking.id}
          businessId={booking.business_id || ''}
          bookingTotal={booking.total_amount || 0}
          amountPaid={booking.payments?.reduce((sum, payment) => 
            payment.status === 'completed' ? sum + payment.amount : sum, 0) || 0}
        />

        <PaymentDetailsModal
          isOpen={isPaymentDetailsModalOpen}
          onClose={() => {
            setIsPaymentDetailsModalOpen(false);
            setSelectedPayment(null);
          }}
          bookingId={booking.id}
          allPayments={allPaymentDetails}
          onUpdate={onUpdate}
        />
      </div>
    );
  }

  if (activeTab === 'materials') {
    return (
      <BookingMaterialsTab
        materials={booking.materials || []}
        onAddMaterial={async (material, quantity) => {
          try {
            const { error: addError } = await supabase!
              .from('booking_materials')
              .insert({
                booking_id: booking.id,
                material_id: material.id,
                material_name: material.name,
                material_description: material.description,
                quantity,
                unit: material.unit,
                cost: material.cost,
                total_cost: material.cost * quantity
              });

            if (addError) throw addError;

            await onUpdate();
          } catch (err) {
            console.error('Error adding material:', err);
            onError(err instanceof Error ? err.message : 'Failed to add material');
          }
        }}
        onRemoveMaterial={async (id) => {
          try {
            const { error: deleteError } = await supabase!
              .from('booking_materials')
              .delete()
              .eq('id', id);

            if (deleteError) throw deleteError;

            await onUpdate();
          } catch (err) {
            console.error('Error removing material:', err);
            onError(err instanceof Error ? err.message : 'Failed to remove material');
          }
        }}
        onUpdate={onUpdate}
        onError={onError}
        onOpenMaterialModal={() => setIsSelectMaterialModalOpen(true)}
        booking={booking}
      />
    );
  }

  if (activeTab === 'flat-rates') {
    return (
      <BookingFlatRatesTab
        bookingId={booking.id}
        businessId={booking.business_id}
        onUpdate={onUpdate}
        onError={onError}
        booking={booking}
      />
    );
  }

  if (activeTab === 'profit-loss') {
    return (
      <BookingProfitLoss
        revenue={booking.subtotal || 0}
        materialCosts={booking.materials?.reduce((sum, material) => 
          sum + (material.total_cost || 0), 0) || 0}
        discounts={booking.discounts?.reduce((sum, discount) => {
          if (discount.type === 'percentage') {
            return sum + (booking.subtotal * discount.amount / 100);
          }
          return sum + discount.amount;
        }, 0) || 0}
        laborCosts={booking.flat_rates?.reduce((sum, rate) => {
          let rateAmount = 0;
          if ((rate as any).resource_flat_rate_amount !== null && (rate as any).resource_flat_rate_amount !== undefined) {
            rateAmount = Number((rate as any).resource_flat_rate_amount);
          } else if ((rate as any).resource_hourly_rate !== null && (rate as any).resource_hourly_rate !== undefined) {
            rateAmount = Number((rate as any).resource_hourly_rate);
          }
          
          return sum + (rate.hours * rateAmount);
        }, 0) || 0}
        bookingId={booking.id}
      />
    );
  }

  return null;
}
